// src/context/SiteContext.jsx
// UPDATED: Now syncs activeSite with sessionStorage so all components can access site_id

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from './AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

export const SiteContext = createContext({
  sites: [],
  activeSite: null,
  setActiveSite: () => {},
  setSites: () => {}
});

export function SiteProvider({ children }) {
  const { token, setToken } = useContext(AuthContext);
  const [sites, setSites] = useState([]);
  const [activeSite, setActiveSiteState] = useState(null);
  
  const activeSiteRef = useRef(activeSite); 
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Keep the ref updated whenever activeSite changes
  useEffect(() => {
    activeSiteRef.current = activeSite;
  }, [activeSite]);

  // ✅ NEW: Wrapper function that syncs both state AND sessionStorage
  const setActiveSite = useCallback((site) => {
    setActiveSiteState(site);
    
    if (site) {
      // Sync to sessionStorage so all components can access
      sessionStorage.setItem('site_id', String(site.id));
      sessionStorage.setItem('site_token', String(site.token || ''));
      sessionStorage.setItem('site_platform', site.platform || '');
      sessionStorage.setItem('site_url', site.url || '');
      //console.log('[SiteContext] Synced site to sessionStorage:', site.id);
    } else {
      // Clear sessionStorage if site is null
      sessionStorage.removeItem('site_id');
      sessionStorage.removeItem('site_token');
      sessionStorage.removeItem('site_platform');
      sessionStorage.removeItem('site_url');
      //console.log('[SiteContext] Cleared site from sessionStorage');
    }
  }, []);

  // ✅ NEW: On mount, try to restore from sessionStorage first
  useEffect(() => {
    const storedSiteId = sessionStorage.getItem('site_id');
    if (storedSiteId && !activeSiteRef.current) {
      //console.log('[SiteContext] Found site_id in sessionStorage:', storedSiteId);
      // We'll validate this against the fetched sites later
    }
  }, []);

  useEffect(() => {
    // Check for token in URL before redirecting
    const urlToken = searchParams.get('token');
    
    // If there's a token in the URL, capture it first
    if (urlToken && !token) {
      //console.log('[SiteContext] Found token in URL, capturing it');
      setToken(urlToken);
      return;
    }
    
    // Only redirect to login if there's no token AND no URL token
    if (!token && !urlToken) {
      navigate('/login', { replace: true });
      return;
    }
    
    // If we have a URL token but context token isn't set yet, wait
    if (!token) {
      return;
    }

    async function loadSites() {
      try {
        const res = await fetch(`${API}/api/sites`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          signal: AbortSignal.timeout(10000)
        });

        if (res.status === 401) {
          //console.warn('Auth error, but keeping session for now');
          throw new Error('Auth failed - will retry');
        }
        
        if (!res.ok) throw new Error(`Status ${res.status}`);
        
        const data = await res.json();
        setSites(data);
        
        if (data.length > 0) {
          // ✅ NEW: Check if we have a stored site_id and it's still valid
          const storedSiteId = sessionStorage.getItem('site_id');
          const storedSite = storedSiteId 
            ? data.find(s => String(s.id) === storedSiteId)
            : null;
          
          if (storedSite && !activeSiteRef.current) {
            // Restore the previously selected site
            //console.log('[SiteContext] Restoring previously selected site:', storedSite.id);
            setActiveSite(storedSite);
          } else if (!activeSiteRef.current) {
            // No stored site or it's invalid, use first site
            //console.log('[SiteContext] Setting first site as active:', data[0].id);
            setActiveSite(data[0]);
          }
        } else {
          // No sites, clear everything and redirect
          setActiveSite(null);
          navigate('/sites', { replace: true });
        }
      } catch (err) {
        console.error('Error loading sites:', err);
        if (err.message.includes('timeout') || err.message.includes('fetch')) {
          //console.log('Network error, keeping user logged in');
          return;
        }
      }
    }

    loadSites();
  }, [token, navigate, searchParams, setToken, setActiveSite]);

  return (
    <SiteContext.Provider value={{ sites, activeSite, setActiveSite, setSites }}>
      {children}
    </SiteContext.Provider>
  );
}

// ✅ NEW: Custom hook for easy access to siteId
export function useSiteId() {
  const { activeSite } = useContext(SiteContext);
  return activeSite?.id || null;
}

// ✅ NEW: Custom hook for full site context
export function useSite() {
  return useContext(SiteContext);
}